import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../config";
import placeimage from "../image/noimage.jpg";
import { Helmet } from "react-helmet-async";
import Loader from "../components/Loader";
import { toast } from "react-toastify";

export default function TrainerDetail() {
  const [trainer, setTrainer] = useState({});
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const params = useParams();
  const navigate = useNavigate();

  const fetchTrainer = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/trainer/${params.slug}`
      );

      setTrainer(response.data.data);
    } catch (error) {
      navigate("/error");
    } finally {
      setLoading(false);
    }
  }, [navigate, params.slug]);

  useEffect(() => {
    fetchTrainer();
    window.scrollTo(0, 0);
  }, [fetchTrainer]);

  const handleConsultationChange = (e) => {
    setConsultationForm({
      ...consultationForm,
      [e.target.name]: e.target.value
    });
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...consultationForm,
        trainer_id: trainer.id
      };
      const response = await axios.post(`${config.apiUrl}/trainer-consultation`, payload);
      if (response.data.success) {
        toast.success('Consultation booked successfully!');
        setShowBookingModal(false);
        setConsultationForm({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to book consultation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  const imageOnError = (event) => {
    event.currentTarget.src = placeimage;
  };

  // Calculate experience years
  const calculateExperience = (startYear) => {
    const currentYear = new Date().getFullYear();
    return currentYear - (startYear || 2010);
  };

  // Format last updated date
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Generate SEO-friendly URL
  const generateProfileUrl = () => {
    const name = trainer.name?.toLowerCase().replace(/\s+/g, '-') || 'trainer';
    const city = trainer.city?.toLowerCase().replace(/\s+/g, '-') || 'india';
    const id = trainer.id || '12345';
    return `/trainer/${name}-${city}-${id}`;
  };

  // Share profile
  const handleShareProfile = () => {
    const url = `${window.location.origin}${generateProfileUrl()}`;
    if (navigator.share) {
      navigator.share({
        title: `${trainer.name} - Personal Trainer in ${trainer.city}`,
        text: `Check out ${trainer.name}'s profile - Expert fitness trainer in ${trainer.city}`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    }
  };

  // Schema markup for SEO
  const personSchema = {
    "@context": "https://schema.org",
    "@type": ["Person", "LocalBusiness"],
    "name": trainer.name,
    "jobTitle": "Certified Personal Trainer",
    "description": trainer.about || `Professional fitness trainer in ${trainer.city} with ${trainer.experience}+ years of experience`,
    "image": trainer.photo,
    "telephone": trainer.phone,
    "email": trainer.email,
    "url": `${window.location.origin}${generateProfileUrl()}`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": trainer.city,
      "addressCountry": "IN"
    },
    "priceRange": "₹₹",
    "areaServed": {
      "@type": "City",
      "name": trainer.city
    },
    "knowsAbout": trainer.skills?.map(s => s.name) || ["Fitness Training", "Weight Loss", "Muscle Building"],
    "hasCredential": trainer.certifications?.map(c => c.name) || []
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{trainer.name} – Personal Trainer in {trainer.city} | Online & Offline Fitness Coaching</title>
        <meta
          name="description"
          content={`${trainer.name} is a certified personal trainer in ${trainer.city} with ${trainer.experience}+ years of experience. Expert in ${trainer.skills?.slice(0, 3).map(s => s.name).join(', ') || 'fitness training, weight loss, muscle building'}. Book your free consultation today!`}
        />
        <meta
          name="keywords"
          content={`personal trainer ${trainer.city}, gym trainer, fitness coach, weight loss trainer ${trainer.city}, online fitness coaching, ${trainer.name}, certified trainer, muscle building coach, nutrition expert ${trainer.city}`}
        />

        {/* Open Graph Tags */}
        <meta property="og:title" content={`${trainer.name} - Personal Trainer in ${trainer.city}`} />
        <meta property="og:description" content={`Expert fitness coaching with ${trainer.experience}+ years experience. Transform your body and health with personalized training programs.`} />
        <meta property="og:image" content={trainer.photo} />
        <meta property="og:url" content={`${window.location.origin}${generateProfileUrl()}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="MeraGym" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${trainer.name} - Personal Trainer`} />
        <meta name="twitter:description" content={`Expert fitness trainer in ${trainer.city}`} />
        <meta name="twitter:image" content={trainer.photo} />

        {/* Canonical URL */}
        <link rel="canonical" href={`${window.location.origin}${generateProfileUrl()}`} />

        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
      </Helmet>

      <div className="trainer-detail-page">
        {/* A. Profile Header Section */}
        <section className="profile-header-section">
          <div className="container">
            <div className="row align-items-center g-4">
              <div className="col-12 col-md-5 col-lg-4">
                <div className="trainer-profile-image-wrapper">
                  <img
                    src={trainer.photo}
                    onError={imageOnError}
                    alt={`${trainer.name} - Personal Trainer in ${trainer.city}`}
                    className="trainer-main-photo"
                    loading="eager"
                  />
                  <div className="verified-badge-overlay">
                    <i className="bi bi-patch-check-fill"></i>
                    <span>Verified Pro</span>
                  </div>
                  <div className="experience-years-badge">
                    {trainer.experience || calculateExperience(2010)}+ Years
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-7 col-lg-8">
                <div className="trainer-header-info">
                  {/* H1 - Trainer Name */}
                  <h1 className="trainer-main-name">{trainer.name}</h1>

                  {/* Location */}
                  <div className="trainer-location">
                    <i className="bi bi-geo-alt-fill"></i>
                    <span>{trainer.city}, {trainer.country || 'India'}</span>
                  </div>

                  {/* Short Headline */}
                  <p className="trainer-headline">
                    {trainer.sort_about || `Certified Personal Trainer specializing in transforming lives through fitness and nutrition`}
                  </p>

                  {/* Specializations Tags */}
                  {trainer.skills && trainer.skills.length > 0 && (
                    <div className="specialization-pills">
                      {trainer.skills.map((skill, index) => (
                        <span key={index} className="spec-pill">
                          <i className="bi bi-check-circle-fill"></i>
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Rating */}
                  {/* <div className="trainer-rating-display">
                    <div className="stars-display">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="bi bi-star-fill"></i>
                      ))}
                    </div>
                    <span className="rating-score">4.9</span>
                    <span className="rating-reviews">(47 reviews)</span>
                    <span className="separator">•</span>
                    <span className="response-badge">
                      <i className="bi bi-lightning-fill"></i>
                      Quick Responder
                    </span>
                  </div> */}

                  {/* Primary CTAs */}
                  <div className="header-cta-buttons">
                    <button className="btn-book-now" onClick={() => setShowBookingModal(true)}>
                      <i className="bi bi-calendar-check-fill"></i>
                      Book Free Consultation
                    </button>
                    <button className="btn-whatsapp" onClick={() => window.open(`https://wa.me/91${trainer.phone}`, '_blank')}>
                      <i className="bi bi-whatsapp"></i>
                      WhatsApp Now
                    </button>
                    <button className="btn-share-profile" onClick={handleShareProfile}>
                      <i className="bi bi-share-fill"></i>
                      Share Profile
                    </button>
                  </div>

                  {/* Trust Badges */}
                  <div className="trust-badges-row">
                    {trainer.is_background_verified && (
                      <div className="trust-badge">
                        <i className="bi bi-shield-fill-check"></i>
                        <span>Background Verified</span>
                      </div>
                    )}
                    {trainer.is_certified_professional && (
                      <div className="trust-badge">
                        <i className="bi bi-award-fill"></i>
                        <span>Certified Professional</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sticky Book Now Bar (Mobile) */}
        <div className="sticky-book-bar">
          <div className="container">
            <div className="sticky-bar-content">
              <div className="sticky-trainer-info">
                <img src={trainer.photo} alt={trainer.name} onError={imageOnError} />
                <div>
                  <strong>{trainer.name}</strong>
                  <span>{trainer.city}</span>
                </div>
              </div>
              <button className="sticky-book-btn" onClick={() => setShowBookingModal(true)}>
                Book Now
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <section className="main-content-section">
          <div className="container">
            <div className="row g-4">
              {/* Left Column - Main Content */}
              <div className="col-12 col-lg-8">
                {/* B. About Trainer Section */}
                <div className="content-block">
                  <h2 className="section-heading-h2">About {trainer.name}</h2>
                  <div className="about-content" dangerouslySetInnerHTML={{ __html: trainer.about }} />

                  {/* Last Updated */}
                  <div className="last-updated-info">
                    <i className="bi bi-clock-history"></i>
                    <span>Profile last updated on {lastUpdated}</span>
                  </div>
                </div>

                {/* C. Expertise & Specializations */}
                <div className="content-block">
                  <h2 className="section-heading-h2">Expertise & Specializations</h2>
                  <div className="expertise-grid">
                    {trainer.skills && trainer.skills.length > 0 ? (
                      trainer.skills.map((skill, index) => (
                        <div key={index} className="expertise-card">
                          <div className="expertise-icon">
                            <i className={skill.icon || "bi-check-circle-fill"}></i>
                          </div>
                          <h3>{skill.name}</h3>
                        </div>
                      ))
                    ) : (
                      <p>No specializations added yet.</p>
                    )}
                  </div>
                </div>

                {/* D. Services & Pricing */}
                <div className="content-block">
                  <h2 className="section-heading-h2">Services & Pricing</h2>
                  <div className="services-pricing-table">
                    {trainer.services && trainer.services.length > 0 ? (
                      trainer.services.map((service, index) => (
                        <div key={index} className="service-pricing-card">
                          <div className="service-header-row">
                            <div>
                              <h3 className="service-name">{service.name || service.displayName}</h3>
                              <div className="service-meta-tags">
                                <span className={`type-badge ${service.type?.toLowerCase()}`}>
                                  {service.type || 'Offline'}
                                </span>
                                <span className="duration-badge">
                                  <i className="bi bi-clock-fill"></i>
                                  {service.duration || service.durationUnit}
                                </span>
                              </div>
                            </div>
                            {/* <div className="service-price-display">
                              <div className="price-amount">{service.price}</div>
                              <div className="price-period">per {service.duration?.includes('Month') ? 'month' : 'session'}</div>
                            </div> */}
                          </div>
                          {service.features && service.features.length > 0 && (
                            <ul className="service-features-list">
                              {service.features.slice(0, 3).map((feature, idx) => (
                                <li key={idx}>
                                  <i className="bi bi-check-circle-fill"></i>
                                  {feature}
                                </li>
                              ))}
                              {service.features.length > 3 && (
                                <li className="more-features-text">
                                  <i className="bi bi-plus-circle-fill"></i>
                                  And {service.features.length - 3} more features...
                                </li>
                              )}
                            </ul>
                          )}
                          <div className="service-card-actions">
                            <Link
                              to={`/service/${params.slug}/${service.id}`}
                              className="btn-view-details"
                            >
                              <i className="bi bi-info-circle-fill"></i>
                              View Details
                            </Link>
                            <button className="btn-book-service" onClick={() => setShowBookingModal(true)}>
                              <i className="bi bi-calendar-check-fill"></i>
                              Book Now
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p>No services listed yet.</p>
                    )}
                  </div>
                </div>

                {/* E. Transformation Gallery */}
                <div className="content-block">
                  <h2 className="section-heading-h2">Transformation Gallery</h2>
                  <div className="transformation-gallery">
                    {trainer.gallery && trainer.gallery.length > 0 ? (
                      trainer.gallery.map((item, index) => (
                        <div key={index} className="gallery-item-card" onClick={() => setActiveImageIndex(index)}>
                          <img
                            src={item.image || item.url}
                            alt={`Transformation ${index + 1} by ${trainer.name}`}
                            onError={imageOnError}
                            loading="lazy"
                          />
                          <div className="gallery-overlay">
                            <i className="bi bi-zoom-in"></i>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-gallery-message">
                        <i className="bi bi-images"></i>
                        <p>Transformation photos coming soon...</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* F. Certifications & Achievements */}
                <div className="content-block">
                  <h2 className="section-heading-h2">Certifications & Achievements</h2>
                  <div className="certifications-grid">
                    {trainer.certifications && trainer.certifications.length > 0 ? (
                      trainer.certifications.map((cert, index) => (
                        <div key={index} className="certification-card">
                          <div className="cert-icon-badge">
                            <i className="bi bi-award-fill"></i>
                          </div>
                          <div className="cert-details">
                            <h4>{cert.name}</h4>
                            <p className="cert-issuer">{cert.issuedBy || cert.issued_by}</p>
                            <p className="cert-year">Certified in {cert.year}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p>No certifications listed.</p>
                    )}
                  </div>
                </div>

                {/* G. Reviews & Ratings */}
                {/* <div className="content-block">
                  <h2 className="section-heading-h2">Client Reviews & Ratings</h2>

                  <div className="rating-summary-box">
                    <div className="overall-rating-display">
                      <div className="rating-number-large">4.9</div>
                      <div className="stars-large-display">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="bi bi-star-fill"></i>
                        ))}
                      </div>
                      <p>Based on 47 reviews</p>
                    </div>
                    <div className="rating-distribution">
                      {[5, 4, 3, 2, 1].map((star) => (
                        <div key={star} className="rating-bar-item">
                          <span className="star-label">{star} <i className="bi bi-star-fill"></i></span>
                          <div className="bar-container">
                            <div className="bar-fill" style={{ width: `${star === 5 ? 85 : star === 4 ? 12 : 3}%` }}></div>
                          </div>
                          <span className="count-label">{star === 5 ? 40 : star === 4 ? 6 : 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="reviews-list-container">
                    {trainer.reviews && trainer.reviews.length > 0 ? (
                      trainer.reviews.map((review, index) => (
                        <div key={index} className="review-card-item">
                          <div className="review-header-row">
                            <div className="reviewer-avatar-circle">
                              {review.name.charAt(0)}
                            </div>
                            <div className="reviewer-info-col">
                              <h4 className="reviewer-name">
                                {review.name}
                                {review.verified && (
                                  <i className="bi bi-patch-check-fill verified-icon" title="Verified Client"></i>
                                )}
                              </h4>
                              <div className="review-meta-row">
                                <div className="review-stars-small">
                                  {[...Array(review.rating)].map((_, i) => (
                                    <i key={i} className="bi bi-star-fill"></i>
                                  ))}
                                </div>
                                <span className="review-date-text">
                                  {new Date(review.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="review-text-content">{review.review}</p>
                          {review.trainerReply && (
                            <div className="trainer-reply-box">
                              <div className="reply-header">
                                <i className="bi bi-reply-fill"></i>
                                <strong>Trainer's Response:</strong>
                              </div>
                              <p>{review.trainerReply}</p>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p>No reviews yet.</p>
                    )}
                  </div>
                </div> */}
              </div>

              {/* Right Column - Sidebar */}
              <div className="col-12 col-lg-4">
                {/* H. Availability & Booking Section */}
                <div className="sidebar-card-sticky">
                  <h3 className="sidebar-card-title">Book Your Session</h3>

                  {/* Available Days */}
                  <div className="availability-section">
                    <h4 className="availability-heading">Available Days</h4>
                    <div className="days-availability-grid">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                        const isAvailable = trainer.availabilities?.some(a => a.day && a.day.toLowerCase().includes(day.toLowerCase()));
                        return (
                          <div key={index} className={`day-badge ${isAvailable ? 'available' : 'unavailable'}`}>
                            {day}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div className="timeslots-section">
                    <h4 className="availability-heading">Time Slots</h4>
                    <div className="time-slots-list">
                      {trainer.availabilities && trainer.availabilities.length > 0 ? (
                        trainer.availabilities.map((slot, index) => (
                          <div key={index} className="time-slot-item">
                            <i className="bi bi-clock"></i>
                            <span>{slot.day}: {slot.startTime} - {slot.endTime}</span>
                          </div>
                        ))
                      ) : (
                        <p>No specific slots listed.</p>
                      )}
                    </div>
                  </div>

                  {/* Timezone */}
                  <div className="timezone-info">
                    <i className="bi bi-globe"></i>
                    <span>Timezone: IST (GMT+5:30)</span>
                  </div>

                  {/* Booking Buttons */}
                  <button className="sidebar-btn-primary" onClick={() => setShowBookingModal(true)}>
                    <i className="bi bi-calendar-check-fill"></i>
                    Book Session Now
                  </button>
                  <button className="sidebar-btn-secondary" onClick={() => setShowEnquiryForm(true)}>
                    <i className="bi bi-telephone-fill"></i>
                    Request Callback
                  </button>
                </div>

                {/* I. Contact Section */}
                <div className="sidebar-card-block">
                  <h3 className="sidebar-card-title">Get in Touch</h3>
                  <div className="contact-methods-list">
                    <button className="contact-method-btn whatsapp" onClick={() => window.open(`https://wa.me/91${trainer.phone}`, '_blank')}>
                      <i className="bi bi-whatsapp"></i>
                      <span>Chat on WhatsApp</span>
                    </button>
                    <button className="contact-method-btn call" onClick={() => window.location.href = `tel:+91${trainer.phone}`}>
                      <i className="bi bi-telephone-fill"></i>
                      <span>Call: +91-{trainer.phone}</span>
                    </button>
                    <button className="contact-method-btn email" onClick={() => window.location.href = `mailto:${trainer.email}`}>
                      <i className="bi bi-envelope-fill"></i>
                      <span>Send Email</span>
                    </button>
                  </div>

                  {/* Send Enquiry Form */}
                  <button className="btn-send-enquiry" onClick={() => setShowEnquiryForm(true)}>
                    <i className="bi bi-send-fill"></i>
                    Send Enquiry
                  </button>
                </div>

                {/* J. Social Links */}
                {(trainer.facebook || trainer.instagram || trainer.youtube) && (
                  <div className="sidebar-card-block">
                    <h3 className="sidebar-card-title">Connect on Social Media</h3>
                    <div className="social-links-grid">
                      {trainer.instagram && (
                        <a href={trainer.instagram} target="_blank" rel="noopener noreferrer" className="social-link-btn instagram">
                          <i className="bi bi-instagram"></i>
                          <span>Instagram</span>
                        </a>
                      )}
                      {trainer.youtube && (
                        <a href={trainer.youtube} target="_blank" rel="noopener noreferrer" className="social-link-btn youtube">
                          <i className="bi bi-youtube"></i>
                          <span>YouTube</span>
                        </a>
                      )}
                      {trainer.facebook && (
                        <a href={trainer.facebook} target="_blank" rel="noopener noreferrer" className="social-link-btn facebook">
                          <i className="bi bi-facebook"></i>
                          <span>Facebook</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Share Profile Card */}
                <div className="sidebar-card-block share-profile-card">
                  <h3 className="sidebar-card-title">Share This Profile</h3>
                  <p className="share-description">Help others discover this amazing trainer!</p>
                  <button className="btn-share-full" onClick={handleShareProfile}>
                    <i className="bi bi-share-fill"></i>
                    Share Profile
                  </button>
                  <div className="share-social-buttons">
                    <button className="share-social-btn facebook" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}>
                      <i className="bi bi-facebook"></i>
                    </button>
                    <button className="share-social-btn twitter" onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location.href}&text=Check out ${trainer.name}`, '_blank')}>
                      <i className="bi bi-twitter"></i>
                    </button>
                    <button className="share-social-btn whatsapp" onClick={() => window.open(`https://wa.me/?text=Check out ${trainer.name} - ${window.location.href}`, '_blank')}>
                      <i className="bi bi-whatsapp"></i>
                    </button>
                    <button className="share-social-btn link" onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied!');
                    }}>
                      <i className="bi bi-link-45deg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="modal-overlay-custom" onClick={() => setShowBookingModal(false)}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h3>Book Your Free Consultation</h3>
              <button className="modal-close-btn" onClick={() => setShowBookingModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="modal-body-custom">
              <p>Fill in your details and we'll get back to you within 24 hours!</p>
              <form className="booking-form" onSubmit={handleConsultationSubmit}>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  className="form-input-custom"
                  value={consultationForm.name}
                  onChange={handleConsultationChange}
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  className="form-input-custom"
                  value={consultationForm.email}
                  onChange={handleConsultationChange}
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone"
                  className="form-input-custom"
                  value={consultationForm.phone}
                  onChange={handleConsultationChange}
                  required
                />
                <select
                  name="service"
                  className="form-input-custom"
                  value={consultationForm.service}
                  onChange={handleConsultationChange}
                  required
                >
                  <option value="">Select Service</option>
                  {trainer.services && trainer.services.length > 0 ? (
                    trainer.services.map((service, idx) => (
                      <option key={idx} value={service.name || service.displayName}>{service.name || service.displayName}</option>
                    ))
                  ) : (
                    <>
                      <option value="1-on-1 Personal Training">1-on-1 Personal Training</option>
                      <option value="Online Coaching">Online Coaching</option>
                      <option value="Group Training">Group Training</option>
                      <option value="Transformation Package">Transformation Package</option>
                    </>
                  )}
                </select>
                <textarea
                  name="message"
                  placeholder="Your Message (Optional)"
                  className="form-textarea-custom"
                  rows="3"
                  value={consultationForm.message}
                  onChange={handleConsultationChange}
                ></textarea>
                <button type="submit" className="btn-submit-booking" disabled={submitting}>
                  <i className="bi bi-send-fill"></i>
                  {submitting ? 'Submitting...' : 'Submit Booking Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enquiry Form Modal */}
      {showEnquiryForm && (
        <div className="modal-overlay-custom" onClick={() => setShowEnquiryForm(false)}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h3>Send Enquiry</h3>
              <button className="modal-close-btn" onClick={() => setShowEnquiryForm(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="modal-body-custom">
              <form className="booking-form">
                <input type="text" placeholder="Your Name" className="form-input-custom" required />
                <input type="tel" placeholder="Your Phone" className="form-input-custom" required />
                <textarea placeholder="Your Question" className="form-textarea-custom" rows="4" required></textarea>
                <button type="submit" className="btn-submit-booking">
                  <i className="bi bi-send-fill"></i>
                  Send Enquiry
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
